import { useCallback, useState, useMemo } from 'react';
import {
  configApiRef,
  identityApiRef,
  useApi,
} from '@mainstage/core-plugin-api';
import useAsync from 'react-use/lib/useAsync';
import { contextProviderFactory } from '../entityProvider.factory';
import {
  Column,
  GenericEntityType,
  GenericProviderType,
  Pagination,
  Provider,
  ProviderFilters,
  TemplateEntity,
  TextFieldValue,
  StringFilter,
  WbResponsiveTableToolbar,
  KindEnum,
  selectTemplateEntity,
  WbSafeNunjucksApi,
  parseDisplayField,
} from '@agilelab/plugin-wb-platform';
import { useColumns } from '../hooks';
import { cleanParams, parseDynamicFieldsSimple } from './utils';
import { fetchMicroservice, ApiSpec } from '../../../utils/microserviceProxy';

const validPageSizes = [5, 10, 25];

interface RemoteEntity extends GenericEntityType {
  id: string;
  name: string;
  kind: KindEnum;
  [key: string]: any;
}

interface RemoteFilters extends ProviderFilters {
  filter?: string;
}

export interface RemoteContextType
  extends GenericProviderType<RemoteEntity[], RemoteFilters> {
  getAllFilteredEntities: (options?: {
    chunkSize?: number;
    onProgress?: (loadedCount: number, totalLoaded: number) => void;
  }) => Promise<RemoteEntity[]>;
}

interface FetchResult {
  items: RemoteEntity[];
  total: number;
  hasNextPage: boolean;
}

const [useRemoteContext, RemoteContextProvider] =
  contextProviderFactory<RemoteContextType>();

export default useRemoteContext;

export interface RemoteProviderProps {
  templateKind: TemplateEntity;
  children: React.ReactNode;
  formContext: any;
}

/**
 * Dynamically generates column definitions based on the actual data structure.
 * Always includes 'id' as the first column, then adds all other fields from the first item.
 * Excludes internal Backstage entity fields to avoid conflicts.
 */
const generateColumnsFromData = (data: any[]): Map<string, string> => {
  if (!data || data.length === 0) {
    return new Map<string, string>([['id', '{{id}}']]);
  }

  const firstItem = data[0];
  const columns = new Map<string, string>();

  columns.set('id', '{{id}}');

  const excludedKeys = new Set([
    'id',
    '__metadata',
    'apiVersion',
    'kind',
    'metadata',
    'spec',
    'originalId',
  ]);

  for (const key of Object.keys(firstItem)) {
    if (!excludedKeys.has(key)) {
      columns.set(key, `{{${key}}}`);
    }
  }

  return columns;
};

/**
 * Transforms any data item into a RemoteEntity format.
 * Requires the item to have an 'id' field. Generates a display label from selectedField
 * or falls back to common display fields (name, label, title, displayName).
 *
 * @param item - The raw data item from the API
 * @param selectedField - Optional field to use as the display label
 * @returns A RemoteEntity with Backstage-compatible structure
 * @throws Error if the item doesn't have an 'id' field
 */
const toRemoteEntity = (item: any): RemoteEntity => {
  if (!item.id) {
    throw new Error('Remote entity must have an id field');
  }

  const label =
    item.name ||
    item.label ||
    item.title ||
    item.displayName ||
    String(item.id);

  return {
    ...item,
    __metadata: { kind: KindEnum.remote, name: label },
  };
};

export const RemoteProvider: React.FC<RemoteProviderProps> = props => {
  const { children, templateKind, formContext } = props;
  const identityApi = useApi(identityApiRef);
  const config = useApi(configApiRef);
  const { apiSpec } = templateKind;

  // Use configured limit from apiSpec if available, otherwise default to 10
  // Ensure the limit is one of the valid Material-UI page size options
  const apiLimit = Number(apiSpec?.retrieval?.params?.limit) || 10;
  const configuredLimit = validPageSizes.includes(apiLimit)
    ? apiLimit
    : validPageSizes.find(size => size >= apiLimit) || 10;

  const [pagination, setPagination] = useState<Pagination>({
    limit: configuredLimit,
    currentPage: 0,
  });

  const isOldBehavior =
    config.getOptionalBoolean(
      'mesh.builder.ui.remoteProvider.oldOffsetBehaviour',
    ) ?? false;

  const [filters, setFilters] = useState<RemoteFilters>({});
  const [dynamicColumns, setDynamicColumns] = useState<Map<string, string>>(
    new Map([['id', '{{id}}']]),
  );
  const columns: Column[] = useColumns(dynamicColumns, templateKind);

  const fetchRemoteEntities = useCallback(
    async (
      currentFilters: RemoteFilters = {},
      page?: number,
      limit?: number,
    ): Promise<FetchResult> => {
      if (!apiSpec?.retrieval)
        return { items: [], total: 0, hasNextPage: false };

      const { params: defaultParams = {} } = apiSpec.retrieval;

      const requestLimit = limit || 10;

      const resolvedDefaults = parseDynamicFieldsSimple(
        formContext,
        defaultParams,
      );
      const params: Record<string, any> = {
        ...resolvedDefaults,
      };

      cleanParams(params);

      const backendBaseUrl = config.getOptionalString('backend.baseUrl') || '';

      try {
        const credentials = await identityApi.getCredentials();

        const { data, hasMore } = await fetchMicroservice({
          backendBaseUrl,
          operation: 'retrieval',
          apiSpec: apiSpec as ApiSpec,
          page,
          limit: requestLimit,
          oldOffsetBehaviour: isOldBehavior,
          filter: currentFilters.filter,
          body: params,
          token: credentials.token,
          checkHasMore: true,
        });

        if (!Array.isArray(data))
          return { items: [], total: 0, hasNextPage: false };

        const validItems = data.filter(
          item => item && item.id !== undefined && item.id !== null,
        );

        const hasNext = hasMore ?? false;
        const itemsToReturn = validItems;

        if (itemsToReturn.length > 0) {
          setDynamicColumns(generateColumnsFromData(itemsToReturn));
        }

        const transformedItems = itemsToReturn
          .map(item => {
            try {
              return toRemoteEntity(item);
            } catch {
              return null;
            }
          })
          .filter((item): item is RemoteEntity => item !== null);

        return {
          items: transformedItems,
          total: transformedItems.length,
          hasNextPage: hasNext,
        };
      } catch {
        return { items: [], total: 0, hasNextPage: false };
      }
    },
    [apiSpec, config, formContext, identityApi, isOldBehavior],
  );

  const fetchParams = useMemo(
    () => ({
      filters,
      page: pagination.currentPage,
      limit: pagination.limit,
    }),
    [filters, pagination.currentPage, pagination.limit],
  );

  // Single call that handles both data and next page check
  const remoteEntitiesState = useAsync(async () => {
    const result = await fetchRemoteEntities(
      fetchParams.filters,
      fetchParams.page,
      fetchParams.limit,
    );

    setPagination(prev => ({
      ...prev,
      countlessOptions: {
        hasNextPage: () => result.hasNextPage ?? false,
        isFirstPage: () => prev.currentPage === 0,
      },
    }));
    return result.items;
  }, [fetchParams, fetchRemoteEntities]);

  const setPaginationWithLimitReset = useCallback(
    (newPagination: Pagination | ((prev: Pagination) => Pagination)) => {
      setPagination(prev => {
        const next =
          typeof newPagination === 'function'
            ? newPagination(prev)
            : newPagination;
        if (prev.limit !== next.limit) {
          return {
            ...next,
            currentPage: 0,
          };
        }
        return next;
      });
    },
    [],
  );

  const fetchAllFilteredEntities = useCallback(
    async (
      currentFilters: RemoteFilters = {},
      chunkSize: number = 50,
      onProgress?: (loadedCount: number, totalLoaded: number) => void,
    ): Promise<RemoteEntity[]> => {
      if (!apiSpec?.retrieval) return [];

      const { params: defaultParams = {} } = apiSpec.retrieval;

      const resolvedDefaults = parseDynamicFieldsSimple(
        formContext,
        defaultParams,
      );

      const allEntities: RemoteEntity[] = [];
      let page = 0;
      let hasMoreData = true;

      try {
        const credentials = await identityApi.getCredentials();
        const backendBaseUrl =
          config.getOptionalString('backend.baseUrl') || '';

        while (hasMoreData) {
          const params: Record<string, any> = {
            ...resolvedDefaults,
          };

          cleanParams(params);

          const {
            ok,
            status,
            data: dataJson,
          } = await fetchMicroservice({
            backendBaseUrl,
            operation: 'retrieval',
            apiSpec: apiSpec as ApiSpec,
            page,
            limit: chunkSize,
            oldOffsetBehaviour: isOldBehavior,
            filter: currentFilters.filter,
            body: params,
            token: credentials.token,
          });

          if (!ok) {
            throw new Error(`Request failed with status ${status}`);
          }

          const data = dataJson;

          if (!Array.isArray(data) || data.length === 0) {
            hasMoreData = false;
            break;
          }

          const validItems = data.filter(
            item => item && item.id !== undefined && item.id !== null,
          );

          if (page === 0 && validItems.length > 0) {
            setDynamicColumns(generateColumnsFromData(validItems));
          }

          const transformedItems = validItems
            .map(item => {
              try {
                return toRemoteEntity(item);
              } catch {
                return null;
              }
            })
            .filter((item): item is RemoteEntity => item !== null);

          allEntities.push(...transformedItems);

          if (onProgress) {
            onProgress(transformedItems.length, allEntities.length);
          }

          if (validItems.length < chunkSize) {
            hasMoreData = false;
          } else {
            page += 1;
          }
        }
        return allEntities;
      } catch (error) {
        throw new Error('Error fetching all filtered entities:', error);
      }
    },
    [apiSpec, config, identityApi, formContext, isOldBehavior],
  );

  const getAllFilteredEntities = useCallback(
    async (
      options: {
        chunkSize?: number;
        onProgress?: (loadedCount: number, totalLoaded: number) => void;
      } = {},
    ): Promise<any[]> => {
      const { chunkSize = 50, onProgress } = options;
      return await fetchAllFilteredEntities(filters, chunkSize, onProgress);
    },
    [fetchAllFilteredEntities, filters],
  );

  const changeFilters = useCallback(
    <K extends keyof RemoteFilters>(key: K, value: RemoteFilters[K]) => {
      setFilters(prev => ({ ...prev, [key]: value }));
      setPagination(prev => ({ ...prev, currentPage: 0 }));
    },
    [],
  );

  const resetFilters = useCallback(() => {
    setFilters({});
    setPagination(prev => ({ ...prev, currentPage: 0 }));
  }, []);

  const contextValue: RemoteContextType = {
    columns,
    entitiesState: remoteEntitiesState,
    filters,
    changeFilters,
    resetFilters,
    pagination,
    setPagination: setPaginationWithLimitReset,
    count: -1,
    nextCursor: undefined,
    prevCursor: undefined,
    templateKind,
    getAllFilteredEntities,
  };

  return (
    <RemoteContextProvider value={contextValue}>
      {children}
    </RemoteContextProvider>
  );
};

export const RemoteFiltersSection = () => {
  const { filters, changeFilters, resetFilters, templateKind } =
    useRemoteContext();

  const filterElement = (key: string) => {
    return (
      <StringFilter
        key={key}
        field={key.charAt(0).toUpperCase() + key.slice(1)}
        value={filters[key] ?? undefined}
        onChange={value => changeFilters(key as keyof RemoteFilters, value)}
      />
    );
  };

  const searchProps = templateKind?.userFilters?.includes('search')
    ? {
        value: filters.filter ?? '',
        onChange: (value: string) => changeFilters('filter', value),
      }
    : undefined;

  const filtersWithoutSearch =
    templateKind?.userFilters?.filter(f => f !== 'search') || [];

  return (
    <WbResponsiveTableToolbar
      onClear={resetFilters}
      marginBottom
      searchProps={searchProps}
    >
      {filtersWithoutSearch.length > 0 && (
        <WbResponsiveTableToolbar.Filters>
          {filtersWithoutSearch.map(filter => filterElement(filter))}
        </WbResponsiveTableToolbar.Filters>
      )}
    </WbResponsiveTableToolbar>
  );
};

export class RemoteHandlerProvider
  implements Provider<RemoteEntity[], RemoteFilters>
{
  private readonly templateEntity: TemplateEntity | undefined;
  private readonly nunjucksApi: WbSafeNunjucksApi;

  constructor(
    nunjucksApi: WbSafeNunjucksApi,
    availableKinds: TemplateEntity[],
  ) {
    this.templateEntity = selectTemplateEntity(availableKinds, KindEnum.remote);
    this.nunjucksApi = nunjucksApi;
  }

  useContext = useRemoteContext;

  renderContextProvider(
    templateKind: TemplateEntity | undefined,
    children: React.ReactNode,
    _: any,
    formContext: any,
  ) {
    return (
      <RemoteProvider templateKind={templateKind!} formContext={formContext}>
        {children}
      </RemoteProvider>
    );
  }

  filtersSection = RemoteFiltersSection;

  extractEntityId(entity: GenericEntityType) {
    return entity.id;
  }

  adapt(entity: GenericEntityType) {
    const parsedDisplayField = parseDisplayField(
      this.nunjucksApi,
      entity,
      this.templateEntity?.displayField,
    );
    const remoteEntity = toRemoteEntity(entity);

    return {
      name: remoteEntity.name,
      kind: KindEnum.remote,
      label: parsedDisplayField ?? remoteEntity.name,
      urn: entity.id,
      // for remote entity only urn or fullObj could be returned
      // for fault tolerance urn will be returned if ref is selected on the template
      ref: entity.id,
    };
  }

  async resolve(ref: string): Promise<TextFieldValue | undefined> {
    if (!this.templateEntity?.apiSpec?.retrieval) return undefined;

    const {
      baseUrl,
      path,
      method = 'POST',
      params: defaultParams = {},
    } = this.templateEntity?.apiSpec?.retrieval;

    const url = baseUrl + path;
    const params: Record<string, any> = {
      // todo this should be parsed with parseDynamicFieldsSimple
      defaultParams,
      id: ref,
    };

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Try to get the token from localStorage (since we don't have access to identityApi here)
    const token = localStorage.getItem('backstage:token');
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    let response;
    if (method.toUpperCase() === 'POST') {
      response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(params),
      });
    } else {
      // Convert all parameter values to strings for URLSearchParams
      const stringParams: Record<string, string> = {};
      Object.entries(params).forEach(([key, value]) => {
        stringParams[key] = String(value);
      });
      const queryString = new URLSearchParams(stringParams).toString();
      response = await fetch(`${url}?${queryString}`, { headers });
    }

    const dataJson = await response.json();

    let item;
    if (Array.isArray(dataJson)) {
      item = dataJson.find(
        (e: any) =>
          e &&
          e.id !== undefined &&
          e.id !== null &&
          String(e.id) === String(ref),
      );
    } else {
      item = dataJson;
    }

    if (!item || !item.id) return undefined;

    try {
      const parsedDisplayField = parseDisplayField(
        this.nunjucksApi,
        item,
        this.templateEntity?.displayField,
      );

      const entity = toRemoteEntity(item);

      return {
        kind: KindEnum.remote,
        label: parsedDisplayField ?? entity.name,
        value: entity.originalId,
      };
    } catch (error) {
      return undefined;
    }
  }
}
